/** @license DAFT Core v1.2.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/
import { map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, combineLatest, of } from 'rxjs';

import { ProcessorFunction, isFieldName, parseFieldIdentifier } from "./processor";
import { ExpressionManager } from './ExpressionManager';
import { ArrayExpression, ConcatExpression, FindByIndexExpression, FindOneExpression, MapExpression } from './expressions';
import { DFSValueType } from './form';
import { hash } from 'hash-it';
import { rescopeDataPath } from '../utils';

export function isArrayExpression(exp: any): exp is ArrayExpression {
    if (isFieldName(exp)) {
      return true;
    }

    if (typeof exp === "string" || typeof exp === "number" || typeof exp === "boolean") {
      return false;
    }

    return exp.mapping !== undefined;
}

export const $map: ProcessorFunction<MapExpression, Array<DFSValueType>> = function(expression, scope, expressionManager) {
  return expressionManager.watch(expression.source, scope)
    .pipe(
      switchMap(e => {
        if (!e) {
          return of([]);
        }
        if (!Array.isArray(e)) {
          throw Error("$map source must be an Array, received '" + e + "'");
        }

        let o: ExpressionManager;
        let path: string;

        if (isFieldName(expression.source)) {
          path = parseFieldIdentifier(expression.source);
          if (path.startsWith('../')) {
            path = parseFieldIdentifier(rescopeDataPath(expression.source, scope));
            
          }
          o = expressionManager;
        } else {
          path = '';
          o = new ExpressionManager(new BehaviorSubject(e));
        }
        if (!e.length) {
          return of([]);
        }
        return combineLatest(e.map(function(_, s) {
          return o.watch(expression.mapping, path.concat("/" + s));
        }))
      })
    )
};

export const $concat: ProcessorFunction<ConcatExpression, Array<DFSValueType>> = function(expression, e, expressionManager) {
  return expressionManager.watch(expression.source, e)
    .pipe(
      switchMap(source => {
        if (!Array.isArray(source)) {
          throw Error("$concat source must be an Array, received '" + source + "'");
        }

        if (!source.length) {
          return of([]);
        }
        let result = source.reduce(
          (acc, curr) => {
            let arr = [...curr];
            if (expression.omitNull) {
              arr = arr.filter(i => i !== null);
            }
            
            acc = acc.concat(arr);
            return acc;
          },
          []
        );
        if (expression.unique) {
          const hashes: string[] = [];
          result = result.filter((item: any) => {
            const h = hash(item).toString();
            const found = hashes.indexOf(h) !== -1;
            hashes.push(h);
            return !found;
          })
        }
        return of(result);
      })
    )
};

/*
export const $filter: ProcessorFunction<FilterExpression, Array<DFSValueType>> = function(r, e, expressionManager) {
  var i = expressionManager.watch(r.source, e) as Observable<any[]>;

  return i.pipe(
    switchMap((items) =>
      from(items).pipe(
        switchMap((item) => {
          const isIncluded: Observable<boolean> = functions[(r.filter as any).op](r.filter, item as string, expressionManager) as Observable<boolean>;
          return isIncluded.pipe(switchMap(isIncluded => isIncluded ? of(item) : EMPTY));
        }),
        toArray(),
      )      
    )
  );
};
*/

export const $findByIndex: ProcessorFunction<FindByIndexExpression, { [id: string]: any; }> = function(r, e, expressionManager) {
  var i = expressionManager.watch(r.index, e) as Observable<any>;

  return i.pipe(
    switchMap((index) => {
      return r.map[index] ? of(r.map[index]) : EMPTY;
    })
  );
};

export const $findOne: ProcessorFunction<FindOneExpression, DFSValueType> = function(expression, scope, expressionManager) {
  return combineLatest(
    [
      expressionManager.watch(expression.source, scope),
      expressionManager.watch(expression.value, scope)
    ])
    .pipe(
      map(e => {
        const source = e[0];
        const value = e[1];

        if (!Array.isArray(source)) {
          return of(null);
        }

        return source.find((item) => item[expression.property] === value) as DFSValueType;
      })
    );
};
